/**
 * Earth Tiled Material
 * TSL-based node material using texture array for tile sampling
 * Dynamically imports WebGPU modules only when available
 */

import '../../../utils/webgpuPolyfill.js'

import * as THREE from 'three'
import materialConfig from '../config/materialConfig.json'
import { DirectionalLight } from 'three'
import { getCachedTextureArray, setCachedTextureArray } from '../textures/textureCache'

// A shared 1x1 black CubeTexture used as a dummy envMap on Earth/cloud node materials.
// THREE.materialEnvIntensity = material.envMap ? material.envMapIntensity : scene.environmentIntensity
// By setting envMap to this dummy and envMapIntensity=0 we get zero IBL regardless of scene.environmentIntensity.
const BLACK_CUBE = new THREE.CubeTexture()
BLACK_CUBE.needsUpdate = false

/**
 * Create a 3D texture (texture array) from individual tile textures
 * @param {Array<Texture>} tileTextures - Array of loaded tile textures
 * @param {number} columns - Grid columns
 * @param {number} rows - Grid rows
 * @param {Object} Data3DTexture - Data3DTexture class from three/webgpu
 * @param {string} textureType - Type of texture (for color space)
 * @returns {Data3DTexture}
 */
function createTextureArray(tileTextures, columns, rows, Data3DTexture, textureType = 'diffuse') {
  if (!tileTextures || tileTextures.length === 0 || !Data3DTexture) return null
  
  // Wait for first texture to load to get dimensions
  const firstTile = tileTextures[0]
  if (!firstTile.image) {
    return null
  }
  
  const tileSize = firstTile.image.width // Assuming square tiles
  const depth = tileTextures.length
  
  // Calculate memory size for warning
  const memorySizeMB = (tileSize * tileSize * depth * 4) / (1024 * 1024)
  
  // Create data array for all tiles
  const data = new Uint8Array(tileSize * tileSize * depth * 4)
  
  // Copy each tile into the array
  tileTextures.forEach((tileTex, index) => {
    if (!tileTex.image) {
      return
    }
    
    const canvas = document.createElement('canvas')
    canvas.width = tileSize
    canvas.height = tileSize
    let ctx = canvas.getContext('2d', { willReadFrequently: true })
    
    // Clear to transparent black
    ctx.clearRect(0, 0, tileSize, tileSize)
    
    // Draw tile to canvas - center it if dimensions don't match
    const imgWidth = tileTex.image.width
    const imgHeight = tileTex.image.height
    
    ctx.drawImage(tileTex.image, 0, 0, imgWidth, imgHeight, 0, 0, tileSize, tileSize)
    
    // Get pixel data
    const imageData = ctx.getImageData(0, 0, tileSize, tileSize)
    
    // Copy to 3D texture data
    const offset = index * tileSize * tileSize * 4
    data.set(imageData.data, offset)
    
    // CRITICAL: Proper GPU memory cleanup for canvas context
    // Clear canvas content
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // Force GPU context flush by reading a single pixel
    // This ensures pending GPU operations complete before releasing context
    try {
      ctx.getImageData(0, 0, 1, 1)
    } catch (e) {
      // Ignore errors during flush
    }
    
    // Release context reference to allow GPU memory cleanup
    ctx = null
    
    // Clean up canvas element
    canvas.width = 0
    canvas.height = 0
    canvas.remove()
  })
  
  // Create 3D texture
  const texture = new Data3DTexture(data, tileSize, tileSize, depth)
  texture.format = THREE.RGBAFormat
  texture.type = THREE.UnsignedByteType
  
  // Set color space based on texture type
  if (textureType === 'diffuse' || textureType === 'nightmap' || textureType === 'clouds') {
    texture.colorSpace = THREE.SRGBColorSpace
  } else {
    texture.colorSpace = THREE.LinearSRGBColorSpace
  }
  
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  texture.wrapR = THREE.ClampToEdgeWrapping
  texture.anisotropy = 16 // Max anisotropic filtering for quality at oblique angles
  texture.unpackAlignment = 1
  texture.needsUpdate = true
  
  return texture
}

/**
 * Create TSL node material with tile-based sampling via texture array
 * @param {Object} options
 * @param {Array<Texture>} options.tileTextures - Array of tile textures  
 * @param {Array<Texture>} options.normalTextures - Array of normal map textures
 * @param {Array<Texture>} options.specularTextures - Array of specular map textures
 * @param {Array<Texture>} options.nightmapTextures - Array of nightmap textures
 * @param {Array<Texture>} options.cloudTextures - Array of cloud textures for shadow baking
 * @param {number} options.columns - Grid columns
 * @param {number} options.rows - Grid rows
 * @param {boolean} options.isWebGPU - Whether WebGPU renderer is being used
 * @param {boolean} options.isClouds - Whether this is a clouds material (transparent)
 * @param {string} options.quality - LOD quality level (e.g., 'LOD_0', 'LOD_1')
 * @returns {Promise<MeshStandardNodeMaterial|MeshStandardMaterial>}
 */
export async function createEarthMaterial({
  tileTextures,
  normalTextures,
  specularTextures,
  nightmapTextures,
  cloudTextures,
  columns,
  rows,
  isWebGPU = true,
  isClouds = false,
  innerAtmosphereConfig = null,
  quality = 'LOD_3',
}) {
  // Use provided config or fall back to materialConfig
  const atmosphereConfig = innerAtmosphereConfig || materialConfig
  // If not WebGPU, use WebGL fallback immediately
  if (!isWebGPU) {
    const material = new THREE.MeshStandardMaterial()
    
    if (tileTextures && tileTextures.length > 0 && tileTextures[0]) {
      material.map = tileTextures[0]
      material.map.flipY = false
    } else {
      material.color = new THREE.Color(0x1f4d78)
    }
    
    material.roughness = 0.8
    material.metalness = 0.1
    material.envMapIntensity = 0 // exclude from scene HDRI
    return material
  }
  
  // Try to load WebGPU modules dynamically
  let webgpuModules = null
  
  try {
    
    // Polyfill GPUShaderStage if not available (iOS Safari WebGPU)
    if (typeof GPUShaderStage === 'undefined' && typeof navigator !== 'undefined' && navigator.gpu) {
      window.GPUShaderStage = {
        VERTEX: 0x1,
        FRAGMENT: 0x2,
        COMPUTE: 0x4
      }
    }
    
    const [tsl, webgpu] = await Promise.all([
      import('three/tsl'),
      import('three/webgpu')
    ])
    
    webgpuModules = {
      texture3D: tsl.texture3D,
      normalMap: tsl.normalMap,
      normalGeometry: tsl.normalGeometry,
      normalWorld: tsl.normalWorld,
      positionWorld: tsl.positionWorld,
      cameraPosition: tsl.cameraPosition,
      positionLocal: tsl.positionLocal,
      normalize: tsl.normalize,
      cross: tsl.cross,
      dot: tsl.dot,
      abs: tsl.abs,
      pow: tsl.pow,
      max: tsl.max,
      min: tsl.min,
      smoothstep: tsl.smoothstep,
      mix: tsl.mix,
      output: tsl.output,
      uv: tsl.uv,
      floor: tsl.floor,
      fract: tsl.fract,
      vec2: tsl.vec2,
      vec3: tsl.vec3,
      vec4: tsl.vec4,
      tslFloat: tsl.float,
      add: tsl.add,
      mul: tsl.mul,
      sub: tsl.sub,
      tslClamp: tsl.clamp,
      uniform: tsl.uniform,
      length: tsl.length,
      distance: tsl.distance,
      MeshStandardNodeMaterial: webgpu.MeshStandardNodeMaterial,
      Data3DTexture: webgpu.Data3DTexture
    }
  } catch (e) {
    // WebGPU modules not available, will use WebGL fallback
  }
  
  // WebGL fallback
  if (!webgpuModules) {
    const material = new THREE.MeshStandardMaterial()
    
    if (tileTextures && tileTextures.length > 0 && tileTextures[0]) {
      material.map = tileTextures[0]
      material.map.flipY = false
    } else {
      material.color = new THREE.Color(0x1f4d78)
    }
    
    material.roughness = 0.8
    material.metalness = 0.1
    material.envMapIntensity = 0 // exclude from scene HDRI
    return material
  }

  const {
    texture3D, normalMap, normalGeometry, normalWorld, positionWorld, cameraPosition, positionLocal, normalize, cross, dot, abs, pow, max, min, smoothstep, mix, output,
    uv, floor, fract, vec2, vec3, vec4, tslFloat, add, mul, sub, tslClamp, uniform,
    MeshStandardNodeMaterial, Data3DTexture
  } = webgpuModules

  // Determine texture type based on isClouds flag
  const textureType = isClouds ? 'clouds' : 'diffuse'
  
  // Check shared cache first - use preloaded texture array if available
  let textureArray = getCachedTextureArray(quality, textureType)
  let normalArray = null
  let specularArray = null
  let nightmapArray = null
  let cloudArray = null
  
  if (!textureArray) {
    // Not in cache, create new texture array from loaded tiles
    if (!tileTextures || tileTextures.length === 0) {
     const material = new MeshStandardNodeMaterial()
      material.colorNode = vec3(0.12, 0.30, 0.48)
      material.roughness = 0.8
      material.metalness = 0.1
      material.envMap = BLACK_CUBE
      material.envMapIntensity = 0 // exclude from scene HDRI
      return material
    }
    
    textureArray = createTextureArray(tileTextures, columns, rows, Data3DTexture, textureType)
    
    if (textureArray) {
      // Add to shared cache
      setCachedTextureArray(quality, textureType, textureArray)
    }
  } else {
  }
  
  // Handle normal maps - check cache first
  normalArray = getCachedTextureArray(quality, 'normal')
  if (!normalArray && normalTextures && normalTextures.length > 0) {
    normalArray = createTextureArray(normalTextures, columns, rows, Data3DTexture, 'normal')
    if (normalArray) {
      setCachedTextureArray(quality, 'normal', normalArray)
    }
  }
  
  // Handle specular maps - check cache first
  specularArray = getCachedTextureArray(quality, 'specular')
  if (!specularArray && specularTextures && specularTextures.length > 0) {
    specularArray = createTextureArray(specularTextures, columns, rows, Data3DTexture, 'specular')
    if (specularArray) {
      setCachedTextureArray(quality, 'specular', specularArray)
    }
  }
  
  // Handle nightmaps - check cache first
  nightmapArray = getCachedTextureArray(quality, 'nightmap')
  if (!nightmapArray && nightmapTextures && nightmapTextures.length > 0) {
    nightmapArray = createTextureArray(nightmapTextures, columns, rows, Data3DTexture, 'nightmap')
    if (nightmapArray) {
      setCachedTextureArray(quality, 'nightmap', nightmapArray)
    }
  }
  
  // Handle cloud shadows (for Earth material only, not clouds material) - check cache first
  if (!isClouds) {
    cloudArray = getCachedTextureArray(quality, 'clouds')
    if (!cloudArray && cloudTextures && cloudTextures.length > 0) {
      cloudArray = createTextureArray(cloudTextures, columns, rows, Data3DTexture, 'clouds')
      if (cloudArray) {
        setCachedTextureArray(quality, 'clouds', cloudArray)
      }
    }
  }
  
  if (!textureArray) {
    const material = new MeshStandardNodeMaterial()
    material.colorNode = vec3(0.12, 0.30, 0.48)
    material.roughness = 0.8
    material.metalness = 0.1
    material.envMap = BLACK_CUBE
    material.envMapIntensity = 0 // exclude from scene HDRI
    return material
  }
  
  const material = new MeshStandardNodeMaterial()
  
  // Configure transparency for clouds
  if (isClouds) {
    material.transparent = true
    material.depthWrite = false
    material.side = THREE.FrontSide
  }

  // Get the actual depth from the texture array
  const textureDepth = textureArray.image.depth
  
  // Create TSL function that samples from texture array
  const uvCoord = uv()
  
  // Flip V coordinate (images have Y=0 at top, UVs have V=0 at bottom)
  const flippedUV = vec2(uvCoord.x, tslFloat(1.0).sub(uvCoord.y))
  
  // Calculate tile indices
  const tileX = floor(mul(flippedUV.x, tslFloat(columns)))
  const tileY = floor(mul(flippedUV.y, tslFloat(rows)))
  
  // Clamp to valid range
  const clampedX = tslClamp(tileX, tslFloat(0), tslFloat(columns - 1))
  const clampedY = tslClamp(tileY, tslFloat(0), tslFloat(rows - 1))
  
  // Local UV within tile
  const localUV = fract(mul(flippedUV, vec2(columns, rows)))
  
  // Calculate flat tile index (which layer in 3D texture)
  const tileIndex = add(mul(clampedY, tslFloat(columns)), clampedX)
  
  // Normalize tile index to 0-1 range for texture array sampling
  const normalizedZ = mul(add(tileIndex, tslFloat(0.5)), tslFloat(1.0 / textureDepth))
  
  // Sample from 3D texture array using (u, v, layer)
  const uvw = vec3(localUV.x, localUV.y, normalizedZ)
  const diffuseSample = texture3D(textureArray, uvw)
  
  // Handle clouds material differently
  if (isClouds) {
    // Calculate fresnel for edge fading
    const viewDirection = positionWorld.sub(cameraPosition).normalize()
    const fresnel = dot(viewDirection, normalWorld).abs().oneMinus()
    
    // Use fresnel to fade clouds at edges (inverted so edges are transparent)
    const edgeFade = tslFloat(1.0).sub(pow(fresnel, tslFloat(2.0)))
    
    // Use the grayscale value as both color and alpha
    const cloudStrength = diffuseSample.r
    material.colorNode = vec3(1.0, 1.0, 1.0) // White clouds
    
    // Combine cloud strength with edge fade, multiplied by fade uniform
    const cloudOpacityFadeUniform = uniform(1.0)
    material.opacityNode = cloudStrength.mul(tslFloat(0.8)).mul(edgeFade).mul(cloudOpacityFadeUniform)
    material.roughness = 0.9
    material.metalness = 0.0
    material.envMap = BLACK_CUBE
    material.envMapIntensity = 0 // exclude from scene HDRI
    
    // Store texture array reference in material for cleanup
    material.userData.textureArray = textureArray
    material.userData.opacityFadeUniform = cloudOpacityFadeUniform
    
    return material
  }
  
  // Get sun orientation using world-space normal (stays fixed as Earth rotates)
  // Create uniform for dynamic sun position
  const lightPositionUniform = uniform(new THREE.Vector3(-5.0, 5.0, 25.0))
  const sunOrientation = dot(normalWorld, normalize(lightPositionUniform))
  
  // Create day strength factor using smoothstep
  // Narrower range = sharper terminator, wider range = more blur
  const dayStrength = smoothstep(tslFloat(materialConfig.terminatorStart), tslFloat(materialConfig.terminatorEnd), sunOrientation)
  
  // Apply cloud shadows to diffuse (darken where clouds are present)
  let finalDiffuse = diffuseSample
  if (cloudArray) {
    const cloudSample = texture3D(cloudArray, uvw)
    const cloudStrength = cloudSample.r
    // Darken diffuse by cloud strength (0.5 = 50% darker where clouds are)
    const cloudShadow = tslFloat(1.0).sub(cloudStrength.mul(tslFloat(0.5)))
    finalDiffuse = diffuseSample.mul(cloudShadow)
  }
  
  // Calculate atmosphere blending (edge glow effect on globe itself)
  const atmosphereDayColor = vec3(0.302, 0.698, 1.0) // #4db2ff
  const atmosphereTwilightColor = vec3(0.737, 0.286, 0.043) // #bc490b
  
  // Calculate fresnel for edge detection
  const viewDirection = positionWorld.sub(cameraPosition).normalize()
  const fresnel = dot(viewDirection, normalWorld).abs().oneMinus()
  
  // Blend between twilight and day colors based on sun orientation
  const atmosphereColor = mix(
    atmosphereTwilightColor,
    atmosphereDayColor,
    smoothstep(tslFloat(-0.25), tslFloat(0.75), sunOrientation)
  )
  
  // Calculate atmosphere day strength for mixing
  const atmosphereDayStrength = smoothstep(tslFloat(-0.5), tslFloat(1.0), sunOrientation)
  
  // Calculate atmosphere mix amount using fresnel from config
  const fresnelAtmosphere = pow(fresnel, tslFloat(atmosphereConfig.innerAtmosphereFresnelPower))
  const atmosphereMixBase = mul(atmosphereDayStrength, fresnelAtmosphere).clamp(0, 1)
  const atmosphereMix = mul(atmosphereMixBase, tslFloat(atmosphereConfig.innerAtmosphereIntensity)).clamp(0, 1)
  
  // Set the base color (without atmosphere)
  material.colorNode = finalDiffuse
  
  // Mix atmosphere into FINAL OUTPUT (after lighting calculations)
  // This is the key - use output.rgb which contains the lit result
  const finalWithAtmosphere = mix(output.rgb, atmosphereColor, atmosphereMix)
  material.outputNode = vec4(finalWithAtmosphere, output.a)
  
  // Blend nightmap into final output on the dark side
  if (nightmapArray) {
    const nightmapSample = texture3D(nightmapArray, uvw)
    // Mix night texture where dayStrength is low (dark side)
    // dayStrength = 1 (full day) shows diffuse only
    // dayStrength = 0 (full night) shows nightmap only
    const nightStrength = dayStrength.oneMinus()
    
    // Apply contrast adjustment: contrast = (value - 0.5) * contrast + 0.5
    const contrastAdjusted = nightmapSample.sub(tslFloat(0.5)).mul(tslFloat(materialConfig.emissiveContrast)).add(tslFloat(0.5))
    
    // Apply brightness multiplier and night strength mask
    material.emissiveNode = contrastAdjusted.mul(tslFloat(materialConfig.emissiveBrightness)).mul(nightStrength)
  }
  
  // Apply normal map the standard TSL way
  if (normalArray) {
    // Sample the normal texture - normalMap() expects the raw texture sample
    const normalTextureSample = texture3D(normalArray, uvw)
    
    // Apply using normalMap() with configurable scale
    material.normalNode = normalMap(normalTextureSample, tslFloat(materialConfig.normalMapScale))
  }
  
  // Apply specular map (controls roughness variation)
  if (specularArray) {
    // Sample specular texture (typically grayscale - use red channel)
    const specularSample = texture3D(specularArray, uvw)
    
    // Specular map controls metalness and roughness with configurable intensity
    // Invert so water (white in spec map) becomes shiny
    const specularValue = specularSample.r.oneMinus().mul(tslFloat(materialConfig.specularIntensity))
    
    // Metalness: interpolate between water (subtle) and land
    // When specularValue = 0 (water): use waterMetalness
    // When specularValue = 1 (land): use landMetalness (typically 0)
    const waterMetal = tslFloat(materialConfig.waterMetalness)
    const landMetal = tslFloat(materialConfig.landMetalness || 0.0)
    const metalnessValue = specularValue.mul(landMetal.sub(waterMetal)).add(waterMetal)
    material.metalnessNode = metalnessValue
    
    // Roughness: interpolate between land (rough) and water (smooth)
    // When specularValue = 0 (water): use waterRoughness
    // When specularValue = 1 (land): use landRoughness
    const waterRough = tslFloat(materialConfig.waterRoughness)
    const landRough = tslFloat(materialConfig.landRoughness)
    const roughnessValue = specularValue.mul(landRough.sub(waterRough)).add(waterRough)
    material.roughnessNode = roughnessValue
  } else {
    material.roughness = materialConfig.baseRoughness
    material.metalness = materialConfig.baseMetalness
  }
  
  material.emissive = new THREE.Color(0x000000)
  material.emissiveIntensity = 1.0 // Full intensity for nightmap
  material.flatShading = false

  // Store texture array reference (cached, so don't dispose)
  material.userData.textureArray = textureArray
  if (normalArray) {
    material.userData.normalArray = normalArray
  }
  if (specularArray) {
    material.userData.specularArray = specularArray
  }
  if (nightmapArray) {
    material.userData.nightmapArray = nightmapArray
  }
  material.userData.isCached = true
  
  // Store light position uniform for dynamic updates
  material.userData.lightPositionUniformNode = lightPositionUniform

  // Opacity uniform for camera-distance fade (default fully opaque)
  const opacityFadeUniform = uniform(1.0)
  material.transparent = true
  material.opacityNode = opacityFadeUniform
  material.userData.opacityFadeUniform = opacityFadeUniform

  // Exclude Earth from scene HDRI environment
  material.envMap = BLACK_CUBE
  material.envMapIntensity = 0

  return material
}

/**
 * Backwards compatibility
 */
export function createEarthMaterials(options) {
  return [createEarthMaterial(options)]
}
